import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DefaultTable } from 'src/components';
import { TableColumn } from 'react-data-table-component';
import { useTranslation } from 'react-i18next';
import { CoverageTypeEnum, IBenefitType } from 'src/interfaces';
import { Loader, RegularButton, SearchInput } from 'pg-ui-library';
import useDebounceSearchTerm from 'src/hooks/useDebounceSearchTerm';
import { PriceFormType } from 'src/services/benefits.service';
import { Container, EmptyContainer } from './styles';

import { useBenefitPrices } from '../../queries/benefit-prices.query';

const priceColumns: TableColumn<PriceFormType>[] = [
  {
    name: 'Benefit Id',
    selector: (row: PriceFormType) => row.benefitId || '',
    sortField: 'benefit ID',

    omit: false,
    sortable: true,
    reorder: true,
    wrap: true,
  },
  {
    name: 'Start Date',
    selector: (row: PriceFormType) => (row.startDate as string) || '',
    sortField: 'startDate',

    omit: false,
    sortable: true,
    reorder: true,
    wrap: true,
  },
  {
    name: 'End Date',
    selector: (row: PriceFormType) => (row.endDate as string) || '',

    sortField: 'endDate',
    omit: false,
    sortable: true,
    reorder: true,
    wrap: true,
  },
];

export const BenefitPriceWrapper: React.FC<{ selectedBenefitType: IBenefitType | undefined }> = ({
  selectedBenefitType,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { searchTerm, setSearchTerm, debouncedValue } = useDebounceSearchTerm(500);

  const { data, isLoading, refetch, isFetching } = useBenefitPrices({
    versionId: selectedBenefitType?.versionId || '',
    search: debouncedValue,
  });

  useEffect(() => {
    refetch();
  }, [debouncedValue]);

  const emptyPriceItem: PriceFormType = {
    benefitId: '',
    versionId: selectedBenefitType?.versionId as string,

    smoke: false,
    type: 'fixed',
    agePriceLines: [],
    fixedPriceLines: [],
    coverage: {
      coverageType: CoverageTypeEnum.NO_COVERAGE,
      employeeCoverageAmount: 0,
    },
  };

  const openPriceForm = (priceItem: PriceFormType = emptyPriceItem) => {
    navigate('/benefits/prices', { state: { initialValue: priceItem, selectedBenefitType } });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', width: '100%', padding: '30px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '30px' }}>
        <SearchInput
          value={searchTerm}
          placeholder="Search..."
          handleChange={event => {
            setSearchTerm(event.target.value);
          }}
        />
        <RegularButton onClick={() => openPriceForm()}>{t('global.form.add')}</RegularButton>
      </div>
      {isLoading || isFetching ? (
        <EmptyContainer>
          <Loader />
        </EmptyContainer>
      ) : (
        <Container>
          <DefaultTable
            columns={priceColumns}
            data={data || []}
            progressPending={isLoading || isFetching}
            pagination={false}
            onRowClicked={row => openPriceForm(row)}
          />
        </Container>
      )}
    </div>
  );
};
